// "What's new since you last looked" — and the Files tile's one line of stats.
//
// Both hub screens (Your Agents, and a Space's Dashboard) show the same three tiles: Routines, Tasks,
// Files. Each carries a badge counting what has appeared since the reader last opened that screen. This
// module is the whole rule, kept import-free so it can be unit tested without the app's dependency graph
// (see CLAUDE.md → "Tests (jest)"), and mirrored 1:1 by `SummaryFreshness.swift` / `SummaryFreshness.kt`.
//
// Two decisions worth stating, because both are the difference between a badge that informs and one that
// misleads:
//
//   1. **An undated item is neither new nor old.** Files written before the store recorded dates arrive
//      with no `createdDate`. Treating a missing date as the epoch would make every one of them
//      permanently "not new" (harmless) — but treating it as *now*, or defaulting it in any way that
//      lands inside the window, would announce a decade-old file as new every time the screen opened.
//      So undated items are excluded from the count entirely, and `hasUndated` lets a caller say so.
//   2. **A first visit is not "everything is new".** With no recorded visit, a badge counting every item
//      the account has ever had would put "247 new" on a screen where nothing happened. The fallback
//      window is the last 24 hours, which is what a first-time reader actually means by "new".

/** How far back "new" reaches for a reader who has never opened the screen on this device. */
export const DEFAULT_NEW_WINDOW_MS = 24 * 60 * 60 * 1000;

/** Anything a freshness count can be taken over. */
export interface DatedItem {
  /** ISO-8601. Absent or unparseable means "unknown", which is never counted as new. */
  createdDate?: string | null;
}

/**
 * The moment "new" starts from: the reader's last visit to this screen, or 24 hours ago when there
 * isn't one.
 *
 * A last-visit stamp in the FUTURE is ignored rather than trusted — a device whose clock was wrong (or
 * corrected backwards) would otherwise suppress the badge forever, and a badge that can never appear is
 * worse than one that is occasionally generous.
 */
export function newSince(lastVisitedMs: number | null | undefined, nowMs: number): number {
  const fallback = nowMs - DEFAULT_NEW_WINDOW_MS;
  if (typeof lastVisitedMs !== 'number' || !Number.isFinite(lastVisitedMs)) return fallback;
  if (lastVisitedMs > nowMs) return fallback;
  return lastVisitedMs;
}

/** Parse an ISO date to epoch ms, or null when it is absent, empty or unparseable. */
export function createdAtMs(item: DatedItem): number | null {
  const raw = item.createdDate;
  if (typeof raw !== 'string' || raw.trim() === '') return null;
  const ms = Date.parse(raw);
  return Number.isNaN(ms) ? null : ms;
}

/** How many items appeared after `sinceMs`. Undated items are not counted — see the note above. */
export function countNewSince(items: DatedItem[], sinceMs: number): number {
  let count = 0;
  for (const item of items) {
    const ms = createdAtMs(item);
    if (ms !== null && ms > sinceMs) count += 1;
  }
  return count;
}

/** A file, as far as the Files tile's stat line is concerned. */
export interface StatFile extends DatedItem {
  /** The file's path WITHIN its workspace, e.g. `artifacts/report.pdf`. */
  name?: string;
  /** Which Space it belongs to. Absent on a workspace-scoped list, where it would be the same for all. */
  workspaceId?: string;
}

/** What the Files tile can say about itself in one line. */
export interface FileStats {
  files: number;
  /** Distinct folders. A file at the workspace root is in no folder and contributes none. */
  folders: number;
  /** Distinct Spaces represented. Only meaningful on the account-wide tile. */
  spaces: number;
  /** Files created since the reader last looked. */
  newCount: number;
}

/** The folder a path sits in, or `''` for a file at the root. Mirrors `folderOf` in `file-browser.ts`. */
function folderOf(path: string): string {
  const cut = path.lastIndexOf('/');
  return cut < 0 ? '' : path.slice(0, cut);
}

/**
 * Fold a file list into the tile's line.
 *
 * Folders are DERIVED — the store is a flat map of path-like keys, and inside a Space the path is
 * entirely the user's to arrange (there is no fixed `artifacts/`-style enum to count against). So this
 * counts distinct folder paths actually in use, and files sitting at the root correctly contribute
 * nothing rather than inventing a phantom "root" folder.
 */
export function fileStats(files: StatFile[], sinceMs: number): FileStats {
  const folders = new Set<string>();
  const spaces = new Set<string>();
  let newCount = 0;
  for (const file of files) {
    const folder = folderOf(file.name ?? '');
    if (folder) folders.add(folder);
    if (file.workspaceId) spaces.add(file.workspaceId);
    const ms = createdAtMs(file);
    if (ms !== null && ms > sinceMs) newCount += 1;
  }
  return { files: files.length, folders: folders.size, spaces: spaces.size, newCount };
}
